/**
 * ツールバーの「背景の分割」ボタン (§20・2026-10-19)。
 *
 * 動画エディタの操作列 (SceneBackgroundColorButton 等) と同じく、アイコン1つをクリックするとポップオーバーに設定を出す。
 * アイコンは現在の設定そのもの (キャンバスの比率の小さな枠に、写真側の塗りと分割線) を描くので、開かなくても
 * 縦割り / 横割り・傾き・位置が見て分かる。未設定なら枠だけ。
 * 分割線の位置と傾きはキャンバス上のドラッグでも変えられる (BannerLayoutCanvas)。ここはその数値入力と、なし / 縦割り / 横割りの切替。
 */
import { useCallback, useRef, useState } from 'react';
import { ArrowLeftRight, ArrowUpDown, Columns2, Rows2 } from 'lucide-react';
import { Popover } from '../../../features/videoEditor/components/Popover';
import { Tooltip } from '../../common/Tooltip';
import { splitLine, splitVisualPolygon } from '../../../features/bannerLayout/geometry';
import { parseRatio, type BannerLayoutBackground } from '../../../features/bannerLayout/types';
import { useEscapeCapture } from '../../../features/bannerLayout/useEscapeCapture';
import { RotationControl } from './BannerLayoutControls';
import styles from './BannerLayout.module.css';

type SplitMode = 'none' | 'vertical' | 'horizontal';
const MODE_LABELS: Record<SplitMode, string> = { none: 'なし', vertical: '縦割り', horizontal: '横割り' };
const OPPOSITE: Record<BannerLayoutBackground['visualSide'], BannerLayoutBackground['visualSide']> = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' };

/** ツールチップ・読み上げ用の現在値 */
function splitSummary(background: BannerLayoutBackground | undefined): string {
  if (!background) return '背景の分割: なし';
  return `背景の分割: ${MODE_LABELS[background.orientation]} (傾き${Math.round(background.angle)}度・位置${Math.round(background.position * 100)}%)`;
}

/** ボタンの絵: キャンバスの比率の小さな枠に、現在の分割 (写真側の塗り + 分割線) を描く */
function SplitGlyph({ background, aspectRatio }: { background: BannerLayoutBackground | undefined; aspectRatio: string }) {
  const BOX_W = 22;
  const BOX_H = 16;
  const r = parseRatio(aspectRatio) ?? 1;
  const w = r >= 1 ? BOX_W : Math.max(6, Math.round(BOX_H * r));
  const h = r >= 1 ? Math.max(6, Math.round(BOX_W / r)) : BOX_H;
  const pts = background ? splitLine(background, w, h) : [];
  return (
    <svg width={BOX_W} height={BOX_H} viewBox={`0 0 ${BOX_W} ${BOX_H}`} aria-hidden="true" className={styles.splitGlyph} data-testid="banner-layout-split-glyph">
      <g transform={`translate(${(BOX_W - w) / 2} ${(BOX_H - h) / 2})`}>
        <rect x={0.5} y={0.5} width={w - 1} height={h - 1} fill="currentColor" fillOpacity={0.12} stroke="currentColor" strokeWidth={1} />
        {background && <polygon points={splitVisualPolygon(background, w, h).map(([x, y]) => `${x},${y}`).join(' ')} fill="currentColor" fillOpacity={0.6} />}
        {pts.length === 2 && <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke="currentColor" strokeWidth={1.5} />}
      </g>
    </svg>
  );
}

export function BannerLayoutSplitButton({
  background,
  aspectRatio,
  onChange,
}: {
  background: BannerLayoutBackground | undefined;
  /** キャンバスの比率 (ボタンの絵の枠の形) */
  aspectRatio: string;
  onChange: (next: BannerLayoutBackground | undefined) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  // Esc はポップオーバーだけを閉じる (モーダルまで閉じない)
  useEscapeCapture(open, close);
  const mode: SplitMode = background?.orientation ?? 'none';
  const setMode = (next: SplitMode) => {
    if (next === mode) return;
    if (next === 'none') onChange(undefined);
    else if (next === 'vertical') onChange({ type: 'split', orientation: 'vertical', angle: background?.angle ?? -12, position: background?.position ?? 0.58, visualSide: 'right' });
    else onChange({ type: 'split', orientation: 'horizontal', angle: background?.angle ?? -12, position: background?.position ?? 0.55, visualSide: 'top' });
  };
  const summary = splitSummary(background);

  return (
    <div ref={rootRef} className={styles.splitButtonWrap} data-testid="banner-layout-background">
      <Tooltip
        content={
          <span>
            <strong>{summary}</strong>
            <br />
            背景を写真側と文字側の2つの領域に分けます。分割線はキャンバス上で掴んで動かせます
          </span>
        }
        placement="bottom"
        disabled={open}
      >
        <button
          type="button"
          className={`${styles.iconButton} ${styles.splitButton} ${open || background ? styles.iconButtonActive : ''}`}
          aria-label={summary}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <SplitGlyph background={background} aspectRatio={aspectRatio} />
        </button>
      </Tooltip>
      <Popover anchorRef={rootRef} open={open} onClose={close} className={styles.splitPopover} role="dialog" ariaLabel="背景の分割" inModal>
        <div className={styles.inspectorRow} role="group" aria-label="分割の向き">
          {(['none', 'vertical', 'horizontal'] as const).map((m) => (
            <button key={m} type="button" className={`${styles.segment} ${styles.segmentWithIcon} ${mode === m ? styles.segmentActive : ''}`} onClick={() => setMode(m)} aria-pressed={mode === m}>
              {m === 'vertical' && <Columns2 size={13} aria-hidden="true" />}
              {m === 'horizontal' && <Rows2 size={13} aria-hidden="true" />}
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>
        {background && (
          <>
            <RotationControl label="分割線の傾き" value={background.angle} onChange={(angle) => onChange({ ...background, angle })} />
            <label className={styles.field}>
              <span>分割線の位置</span>
              <div className={styles.rotationRow}>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(background.position * 100)}
                  onChange={(e) => onChange({ ...background, position: Number(e.target.value) / 100 })}
                  aria-label="分割線の位置"
                />
                <span className={styles.rotationValue}>{Math.round(background.position * 100)}%</span>
              </div>
            </label>
            <button
              type="button"
              className={`${styles.segment} ${styles.segmentWithIcon}`}
              onClick={() => onChange({ ...background, visualSide: OPPOSITE[background.visualSide] } as BannerLayoutBackground)}
              data-testid="banner-layout-split-swap"
            >
              {background.orientation === 'vertical' ? <ArrowLeftRight size={13} aria-hidden="true" /> : <ArrowUpDown size={13} aria-hidden="true" />}
              写真と文字を入れ替える
            </button>
            <span className={styles.inspectorHint}>濃いグレーの側に写真、薄い側に文字が載ります。キャンバスの分割線は掴んで動かし、両端のつまみで回せます (中心・0度に吸着。Option / Cmd / Ctrl で解除)。</span>
          </>
        )}
      </Popover>
    </div>
  );
}
